// src/utils/standardMerger.js

import { isParentFacingRating } from "./teacher/InputRatingUtils";

/**
 * Merges a new Evidence Outcome into an existing one, prioritizing non-null ratings.
 * @param {EvidenceOutcome} existingEo
 * @param {EvidenceOutcome} newEo
 * @returns {EvidenceOutcome}
 */
const mergeEvidenceOutcomes = (existingEo, newEo) => ({
    ...existingEo,
    ...newEo,
    // Use the non-null rating from either object. This combines contentRating from source A
    // and diffRating from source B, if they exist.
    contentRating: existingEo.contentRating || newEo.contentRating,
    diffRating: existingEo.diffRating || newEo.diffRating
});

/**
 * Merges new GLE evidence outcomes into an existing GLE.
 * @param {GLE} existingGle
 * @param {GLE} newGle
 * @returns {GLE}
 */
const mergeGles = (existingGle, newGle) => {
    const eoMap = (existingGle.evidenceOutcomes || []).reduce((acc, eo) => {
        acc[eo.eoId] = eo;
        return acc;
    }, {});

    (newGle.evidenceOutcomes || []).forEach((newEo) => {
        const eoId = newEo.eoId;
        if (eoId in eoMap) {
            eoMap[eoId] = mergeEvidenceOutcomes(eoMap[eoId], newEo);
        } else {
            eoMap[eoId] = newEo;
        }
    });

    return {
        ...existingGle,
        ...newGle,
        gleBody: existingGle.gleBody || newGle.gleBody,
        contentRating: existingGle.contentRating || newGle.contentRating,
        diffRating: existingGle.diffRating || newGle.diffRating,
        evidenceOutcomes: Object.values(eoMap)
    };
};

const contentTypeOf = (diffRating) => String(diffRating?.contentType || "").toUpperCase();

const preferNamed = (map, id, node, nameKey) => {
    const existing = map.get(id);
    if (!existing || (!existing[nameKey] && node[nameKey])) {
        map.set(id, node);
    }
};

const indexHierarchy = (standards) => {
    const gleById = new Map();
    const eoById = new Map();
    const eocById = new Map();
    for (const standard of standards) {
        for (const gle of standard.gles || []) {
            if (gle.gleId != null) preferNamed(gleById, String(gle.gleId), gle, "gleBody");
            for (const eo of gle.evidenceOutcomes || []) {
                if (eo.eoId != null) preferNamed(eoById, String(eo.eoId), eo, "eoBody");
                for (const child of eo.evidenceOutcomeChildren || []) {
                    if (child.eocId != null) preferNamed(eocById, String(child.eocId), child, "eocBody");
                }
            }
        }
    }
    return { gleById, eoById, eocById };
};

/**
 * Profile payloads attach GLE-level (and unmatched EO) diffs under a dummy
 * standard/GLE with null ids and names. Move those ratings onto the real node
 * when contentId matches.
 */
const rehomeOrphanDiffRatings = (standards) => {
    const { gleById, eoById, eocById } = indexHierarchy(standards);

    const rehomeFrom = (holder) => {
        const dr = holder.diffRating;
        if (!dr || dr.contentId == null) return;
        const type = contentTypeOf(dr);
        const id = String(dr.contentId);
        let target = null;
        if (type === "GLE") target = gleById.get(id);
        else if (type === "EVIDENCE_OUTCOME") target = eoById.get(id);
        else if (type === "EVIDENCE_OUTCOME_CHILD") target = eocById.get(id);
        if (!target || target === holder) return;
        if (!target.diffRating) target.diffRating = dr;
        holder.diffRating = null;
    };

    for (const standard of standards) {
        for (const gle of standard.gles || []) {
            rehomeFrom(gle);
            for (const eo of gle.evidenceOutcomes || []) {
                rehomeFrom(eo);
                for (const child of eo.evidenceOutcomeChildren || []) {
                    rehomeFrom(child);
                }
            }
        }
    }
    return standards;
};

const nodeHasRating = (node) =>
    isParentFacingRating(node?.contentRating?.rating) || isParentFacingRating(node?.diffRating?.rating);

const eoHasVisibleContent = (eo) =>
    Boolean(
        eo.eoBody ||
            eo.eocBody ||
            nodeHasRating(eo) ||
            (eo.evidenceOutcomeChildren || []).some((child) => child.eocBody || nodeHasRating(child))
    );

const gleHasVisibleContent = (gle) =>
    Boolean(gle.gleBody || nodeHasRating(gle) || (gle.evidenceOutcomes || []).some(eoHasVisibleContent));

/**
 * Drop dummy standards/GLEs/EOs left after rehoming (null names, no remaining ratings).
 */
const pruneEmptyNodes = (standards) =>
    standards
        .map((standard) => ({
            ...standard,
            gles: (standard.gles || [])
                .map((gle) => ({
                    ...gle,
                    evidenceOutcomes: (gle.evidenceOutcomes || []).filter(eoHasVisibleContent)
                }))
                .filter(gleHasVisibleContent)
        }))
        .filter((standard) => (standard.gles || []).length > 0);

const normalizeStandardBody = (body) =>
    String(body || "")
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .replace(/\.+$/, "")
        .trim()
        .toLowerCase();

const findExistingStandardKey = (mergedStandardsMap, standard) => {
    if (standard.cdestandardId != null && standard.cdestandardId !== "") {
        const idKey = `id:${standard.cdestandardId}`;
        if (mergedStandardsMap[idKey]) return idKey;
        const body = normalizeStandardBody(standard.cdestandardBody);
        if (body) {
            const match = Object.keys(mergedStandardsMap).find(
                (key) => normalizeStandardBody(mergedStandardsMap[key].cdestandardBody) === body
            );
            if (match) return match;
        }
        return idKey;
    }
    const body = normalizeStandardBody(standard.cdestandardBody);
    if (body) {
        const match = Object.keys(mergedStandardsMap).find(
            (key) => normalizeStandardBody(mergedStandardsMap[key].cdestandardBody) === body || key === `body:${body}`
        );
        if (match) return match;
        return `body:${body}`;
    }
    return null;
};

const mergeStandardFields = (target, incoming) => {
    if (target.cdestandardId == null && incoming.cdestandardId != null) {
        target.cdestandardId = incoming.cdestandardId;
    }
    if (!target.cdestandardBody && incoming.cdestandardBody) {
        target.cdestandardBody = incoming.cdestandardBody;
    }
};

/**
 * Combines all CDE Standards, GLEs, and Evidence Outcomes from multiple grading criteria
 * into a single de-duplicated and consolidated list.
 * @param {GradingCriteria[]} data
 * @returns {CDEStandard[]}
 */
export const mergeStandards = (data) => {
    const mergedStandardsMap = {};
    let namelessIndex = 0;

    (data || []).forEach((criteria) => {
        (criteria.cdeStandards || []).forEach((standard) => {
            const standardKey = findExistingStandardKey(mergedStandardsMap, standard) || `anon:${namelessIndex++}`;

            if (!mergedStandardsMap[standardKey]) {
                mergedStandardsMap[standardKey] = {
                    ...standard,
                    gles: (standard.gles || []).reduce((acc, gle) => {
                        acc[gle.gleId] = gle;
                        return acc;
                    }, {})
                };
            } else {
                const mergedStandard = mergedStandardsMap[standardKey];
                mergeStandardFields(mergedStandard, standard);

                (standard.gles || []).forEach((gle) => {
                    const gleId = gle.gleId;
                    if (gleId in mergedStandard.gles) {
                        mergedStandard.gles[gleId] = mergeGles(mergedStandard.gles[gleId], gle);
                    } else {
                        mergedStandard.gles[gleId] = gle;
                    }
                });
            }
        });
    });

    const merged = Object.values(mergedStandardsMap).map((standard) => ({
        ...standard,
        gles: Object.values(standard.gles)
    }));

    return pruneEmptyNodes(rehomeOrphanDiffRatings(merged));
};
